import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/supabase';
import { useUpdateUser } from '../../lib/supabase/users/supabaseUserService';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';

import { useAuth } from '../../contexts/AuthContext';
import { DateTime } from 'luxon';

const Setup = (): JSX.Element => {
  const [full_name, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);
  const navigate = useNavigate();
  const updateUser = useUpdateUser();
  const { user, refetchUserData } = useAuth();

  const isValidName = (name: string): boolean => {
    // Only allow alphabetic characters and spaces
    return /^[a-zA-Z\s]+$/.test(name);
  };

  const validateName = (): string | null => {
    if (!full_name.trim()) return 'Full name is required';
    if (!isValidName(full_name)) return 'Name can only contain alphabetic characters and spaces';
    if (full_name.trim().length < 2) return 'Name must be at least 2 characters long';
    return null;
  };

  const passwordRequirements = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[^a-zA-Z0-9]/.test(password),
  };

  const validatePassword = (): string | null => {
    if (!passwordRequirements.minLength) return 'Password must be at least 8 characters';
    if (!passwordRequirements.hasUpperCase) return 'Password must contain an uppercase letter';
    if (!passwordRequirements.hasLowerCase) return 'Password must contain a lowercase letter';
    if (!passwordRequirements.hasNumber) return 'Password must contain a number';
    if (!passwordRequirements.hasSpecialChar) return 'Password must contain a special character';
    return null;
  };

  const validateDateOfBirth = (): string | null => {
    if (!dateOfBirth) return 'Date of birth is required';
    
    // Check if the date format is valid (DD/MM/YYYY)
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    if (!dateRegex.test(dateOfBirth)) {
      return 'Please enter date in DD/MM/YYYY format';
    }

    // Parse the date
    const [day, month, year] = dateOfBirth.split('/').map(Number);
    const dob = DateTime.utc(year, month, day);

    // Check if the date is valid (e.g., 31/02/2024 would be invalid)
    if (dob.day !== day || dob.month !== month || dob.year !== year) {
      return 'Please enter a valid date';
    }

    const today = DateTime.utc();
    const age = Math.floor(today.diff(dob, 'years').years);
    if (age < 18) return 'You must be at least 18 years old to register';
    if (age > 150) return 'Please enter a valid date of birth';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSettingUp || !user) return;

    const nameError = validateName();
    if (nameError) {
      setError(nameError);
      return;
    }

    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    const dobError = validateDateOfBirth();
    if (dobError) {
      setError(dobError);
      return;
    }

    setIsSettingUp(true);
    setError('');
    
    try {
      // Update password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password
      });

      if (passwordError) {
        console.error('Error updating password:', passwordError);
        setError('Failed to update password. Please try again.');
        setIsSettingUp(false);
        return;
      }

      // Parse the date for storage
      const [day, month, year] = dateOfBirth.split('/').map(Number);
      const dob = DateTime.utc(year, month, day);

      // Update user document
      await updateUser.mutateAsync({
        id: user.id,
        user: {
          full_name: full_name.trim(),
          date_of_birth: dob,
        }
      });

      // Update user metadata to remove trial flag
      if (user.user_metadata.trial) {
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { trial: false }
        });
        if (metadataError) {
          console.error('Error updating user metadata:', metadataError);
          setError('Failed to complete setup. Please try again.');
          setIsSettingUp(false);
          return;
        }
      }

      if (user.user_metadata.needs_setup) {
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { needs_setup: false }
        });
        if (metadataError) {
          console.error('Error updating user metadata:', metadataError);
          setError('Failed to complete setup. Please try again.');
          setIsSettingUp(false);
          return;
        }
      }

      await refetchUserData();
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Failed to complete setup:', err);
      setError('Failed to complete setup. Please try again.');
      setIsSettingUp(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Complete Your Account Setup</CardTitle>
          <CardDescription>
            Please provide your details to complete your account setup.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 w-full">
            <div className="space-y-2 w-full">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                type="text"
                id="full_name"
                placeholder="Enter your full name"
                value={full_name}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFullName(newValue);
                  const nameError = validateName();
                  if (nameError) {
                    setError(nameError);
                  } else {
                    setError('');
                  }
                }}
                required
              />
              <div className="flex items-center gap-2 mt-2 text-sm">
                {isValidName(full_name) && full_name.trim().length >= 2 ? (
                  <Check className="h-4 w-4 text-brand-green" />
                ) : (
                  <X className="h-4 w-4 text-muted-foreground" />
                )}
                <span className={cn(
                  "text-muted-foreground",
                  isValidName(full_name) && full_name.trim().length >= 2 && "text-brand-green"
                )}>
                  Valid name format (alphabetic characters only)
                </span>
              </div>
            </div>

            <div className="space-y-2 w-full">
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              {password && (
                <div className="space-y-2 mt-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      {passwordRequirements.minLength ? (
                        <Check className="h-4 w-4 text-brand-green shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={cn(
                        "text-muted-foreground",
                        passwordRequirements.minLength && "text-brand-green"
                      )}>
                        8+ characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordRequirements.hasUpperCase ? (
                        <Check className="h-4 w-4 text-brand-green shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={cn(
                        "text-muted-foreground",
                        passwordRequirements.hasUpperCase && "text-brand-green"
                      )}>
                        Uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordRequirements.hasLowerCase ? (
                        <Check className="h-4 w-4 text-brand-green shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={cn(
                        "text-muted-foreground",
                        passwordRequirements.hasLowerCase && "text-brand-green"
                      )}>
                        Lowercase letter
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordRequirements.hasNumber ? (
                        <Check className="h-4 w-4 text-brand-green shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={cn(
                        "text-muted-foreground",
                        passwordRequirements.hasNumber && "text-brand-green"
                      )}>
                        Number
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm col-span-2">
                      {passwordRequirements.hasSpecialChar ? (
                        <Check className="h-4 w-4 text-brand-green shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={cn(
                        "text-muted-foreground",
                        passwordRequirements.hasSpecialChar && "text-brand-green"
                      )}>
                        Special character
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 w-full">
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                type="text"
                id="dateOfBirth"
                placeholder="DD/MM/YYYY"
                value={dateOfBirth}
                onChange={(e) => {
                  // Allow only numbers and forward slashes
                  const value = e.target.value.replace(/[^\d/]/g, '');
                  
                  // Automatically add slashes after day and month
                  let formattedValue = value;
                  if (value.length === 2 && dateOfBirth.length !== 3) {
                    formattedValue = value + '/';
                  } else if (value.length === 5 && dateOfBirth.length !== 6) {
                    formattedValue = value + '/';
                  }
                  
                  // Limit the total length to 10 characters (DD/MM/YYYY)
                  if (formattedValue.length <= 10) {
                    setDateOfBirth(formattedValue);
                  }
                }}
                required
              />
              <span className="text-sm text-muted-foreground">
                Enter your date of birth in DD/MM/YYYY format
              </span>
            </div>
            <Button
              type="submit"
              className="w-full"
              variant="default"
              disabled={isSettingUp}
            >
              {isSettingUp ? 'Completing Setup...' : 'Complete Setup'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Setup;
